import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import SEOHead, { SITE_URL } from "@/components/SEOHead";
import SignupModal from "@/components/SignupModal";

const DailyAINews = () => {
  const [editions, setEditions] = useState<any[]>([]);
  const [signupOpen, setSignupOpen] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("editions")
        .select("id, slug, edition_date, hook")
        .in("status", ["approved", "sent"])
        .order("edition_date", { ascending: false })
        .limit(3);
      if (data) setEditions(data);
    };
    fetch();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Daily AI News Summary — Free Email Digest | aibly"
        description="Free daily AI news summary delivered to your inbox. 2-minute digest covering the latest artificial intelligence breakthroughs, tools, and industry moves."
        canonical="/daily-ai-news"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Daily AI News Summary — aibly",
            description: "Free daily AI news summary delivered to your inbox. 2-minute digest of the most important artificial intelligence stories.",
            url: `${SITE_URL}/daily-ai-news`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Daily AI News", item: `${SITE_URL}/daily-ai-news` },
            ],
          },
        ]}
      />
      <Navbar onSubscribeClick={() => setSignupOpen(true)} />

      <article className="pt-24 pb-16 px-4 max-w-2xl mx-auto space-y-10">
        <header className="space-y-4">
          <nav className="text-xs text-muted-foreground tracking-widest uppercase">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-foreground">Daily AI News</span>
          </nav>

          <h1 className="font-display text-3xl md:text-5xl italic text-foreground leading-tight">
            Daily AI News Summary — Delivered Free to Your Inbox
          </h1>

          <p className="text-muted-foreground text-sm md:text-base leading-relaxed max-w-xl">
            AI moves too fast to track on your own. aibly reads hundreds of sources so you don't have to, then sends you the 5-7 stories that actually matter. Takes 2 minutes. Delivered on your schedule.
          </p>
        </header>

        <section className="space-y-4">
          <h2 className="text-xs tracking-widest uppercase text-muted-foreground">
            What You Get Every Day
          </h2>
          <div className="space-y-3">
            {[
              { title: "Curated AI stories", desc: "5 to 7 sourced items. LLMs, robotics, regulation, funding, open-source. Each one linked to the original." },
              { title: "Stat of the Day", desc: "One number that tells you where AI actually stands today." },
              { title: "Hot Take", desc: "An opinion on the biggest story. You can vote agree or disagree." },
              { title: "Weekly Quiz", desc: "Fridays only. Questions pulled from the week's editions." },
            ].map((item) => (
              <div key={item.title} className="border border-border hover:border-muted-foreground transition-colors p-4 space-y-1">
                <h3 className="text-sm font-medium text-foreground">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xs tracking-widest uppercase text-muted-foreground">
            Why a Daily AI News Email?
          </h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              New models drop weekly. Companies pivot overnight. Regulations appear out of nowhere. If you're checking X threads and blog posts to stay current, you already know it's a losing game.
            </p>
            <p>
              aibly watches the firehose and pulls out what's worth knowing. You pick when it lands in your inbox. Read it, and you're caught up. That's it.
            </p>
            <p>
              Every story comes with context and a source link. You'll know what happened and why it matters, not just that it happened.
            </p>
          </div>
        </section>

        {editions.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-xs tracking-widest uppercase text-muted-foreground">
              Recent Editions
            </h2>
            <div className="space-y-2">
              {editions.map((ed) => (
                <Link
                  key={ed.id}
                  to={`/edition/${ed.slug || ed.id}`}
                  className="block border border-border hover:border-muted-foreground transition-colors p-4"
                >
                  <time className="text-[10px] text-muted-foreground tracking-widest uppercase">
                    {format(new Date(ed.edition_date + "T00:00:00"), "EEEE, MMMM d, yyyy")}
                  </time>
                  {ed.hook && (
                    <p className="font-display text-foreground italic text-sm mt-1 line-clamp-1">{ed.hook}</p>
                  )}
                </Link>
              ))}
            </div>
            <Link to="/archive" className="text-xs text-accent hover:underline tracking-widest uppercase">
              Browse full archive →
            </Link>
          </section>
        )}

        <div className="border border-border p-8 text-center space-y-4">
          <p className="font-display text-foreground italic text-lg md:text-xl leading-snug">
            10 seconds to subscribe. 2 minutes a day to stay ahead.
          </p>
          <p className="text-xs text-muted-foreground tracking-wider uppercase">
            Free · No spam · Delivered daily
          </p>
          <Button
            onClick={() => setSignupOpen(true)}
            className="btn-shine rounded-none text-xs tracking-widest uppercase px-10 py-5 gap-2"
          >
            Subscribe Free <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </article>

      <SignupModal open={signupOpen} onOpenChange={setSignupOpen} />
    </div>
  );
};

export default DailyAINews;
